import type { Ring, Vec2 } from '../../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../../mesh/meshBuilder';
import { boxSolid, horizontalCap, subFrame, pyramidOverRing } from '../solids';
import { corners } from './genericBlock';
import { gableWall, rectWall } from './masonry';
import { gableRoof } from '../roofSolids';
import { toLayout, type PlanFrame } from './planFrame';

export interface DamBuilding {
  /** The middle of the house in the dam's plan: `u` from bank to bank, `v` along the river. */
  readonly u: number;
  readonly v: number;
  readonly front: number;
  readonly depth: number;
  readonly eaves: number;
  readonly pitch: number;
  readonly roof: 'gable' | 'slate-gable';
  /** The way its gables look: along the river to the Damrak's water, or across it to the crest. */
  readonly faces: 'damrak' | 'crest';
}

/**
 * The houses on the dam as the 1538 painting draws them: three tall gabled houses on the Damrak's
 * head at its west end, the narrowest and tallest beside the sluice arch, and behind them a row
 * along the west side of the crest to the Rokin's head, gables to the fish market, each a drip's
 * width from the next: its northern half in one line on the dam's west edge, its southern half
 * stepping east with the river's west bank. The ground between the three and the row leads to the
 * square. The row's count, every dimension and the roof materials are estimates.
 */
const TALL = { v: 26.9, depth: 13 } as const;
const ROW = { front: 5.4, depth: 10 } as const;
type Roof = DamBuilding['roof'];

const tall = (u: number, front: number, eaves: number, pitch: number, roof: Roof): DamBuilding => ({
  u,
  v: TALL.v,
  front,
  depth: TALL.depth,
  eaves,
  pitch,
  roof,
  faces: 'damrak',
});

const inRow = (u: number, v: number, eaves: number, pitch: number, roof: Roof): DamBuilding => ({
  u,
  v,
  front: ROW.front,
  depth: ROW.depth,
  eaves,
  pitch,
  roof,
  faces: 'crest',
});

export const DAM_BUILDINGS: readonly DamBuilding[] = [
  tall(-25.45, 5, 9.4, 0.98, 'gable'),
  tall(-19.95, 5, 10.6, 0.95, 'gable'),
  tall(-14.55, 4.8, 12.7, 1.05, 'slate-gable'),
  inRow(-24, 13.1, 8.2, 0.98, 'gable'),
  inRow(-24, 7.5, 9, 0.95, 'slate-gable'),
  inRow(-24, 1.9, 8.4, 1, 'gable'),
  inRow(-24, -3.7, 9.6, 0.95, 'gable'),
  inRow(-23.55, -9.3, 8, 1, 'gable'),
  inRow(-22.55, -14.9, 8.8, 0.98, 'slate-gable'),
  inRow(-21.45, -20.5, 9.2, 0.95, 'gable'),
  inRow(-20.35, -26.1, 8.4, 1, 'gable'),
];

/** The turn of a house's own frame in the dam's plan: its ridge runs along the way it faces. */
const turnOf = (b: DamBuilding): number => (b.faces === 'damrak' ? Math.PI / 2 : 0);

/** Cross windows and capped sills share the wall opening's exact stations on every facade. */
function crossWindows(
  builder: MeshBuilder,
  trim: VertexStyle,
  frame: Frame,
  from: Vec2,
  to: Vec2,
  levels: readonly number[],
  bays: number
): void {
  const length = Math.hypot(to[0] - from[0], to[1] - from[1]);
  const face = subFrame(frame, from[0], from[1], Math.atan2(to[1] - from[1], to[0] - from[0]));
  for (const y of levels)
    for (let k = 0; k < bays; k++) {
      const x = ((k + 0.5) * length) / bays;
      boxSolid(builder, trim, face, [x - 0.67, y - 0.12, -0.14], [x + 0.67, y + 0.02, 0.06], {
        top: true,
        bottom: true,
      });
      boxSolid(builder, trim, face, [x - 0.58, y + 0.73, -0.07], [x + 0.58, y + 0.83, 0.04], {
        top: true,
        bottom: true,
      });
      boxSolid(builder, trim, face, [x - 0.05, y, -0.08], [x + 0.05, y + 1.65, 0.05], {
        top: true,
        bottom: true,
      });
    }
  boxSolid(builder, trim, face, [-0.03, 4.4, -0.16], [length + 0.03, 4.58, 0.04], {
    top: true,
    bottom: true,
  });
}

export function buildDamBuildings(
  builder: MeshBuilder,
  styles: {
    wall: VertexStyle;
    roof: VertexStyle;
    slate: VertexStyle;
    window: VertexStyle;
    trim: VertexStyle;
  },
  frame: Frame,
  crestY: number
): void {
  for (const b of DAM_BUILDINGS) {
    const hf = subFrame(frame, b.u, b.v, turnOf(b), crestY);
    const rect = { u0: -b.depth / 2, u1: b.depth / 2, v0: -b.front / 2, v1: b.front / 2 };
    const wall = { ...styles.wall, extent: [b.front, b.eaves] as const };
    const roof = {
      ...(b.roof === 'slate-gable' ? styles.slate : styles.roof),
      extent: [b.depth, b.front] as const,
    };
    const masonry = { wall, roof, opening: styles.window, glazing: { reveal: styles.trim } };
    const ring = corners(rect);
    ring.forEach((from, side) => {
      const to = ring[(side + 1) % ring.length]!;
      const length = Math.hypot(to[0] - from[0], to[1] - from[1]);
      const end = side % 2 === 1;
      const bays = end ? (b.front < 7 ? 1 : 3) : Math.max(2, Math.floor(length / 4));
      const opening = (x: number, y: number, width: number, height: number): Ring => [
        [x - width / 2, y],
        [x + width / 2, y],
        [x + width / 2, y + height],
        [x - width / 2, y + height],
      ];
      const levels = b.eaves > 12 ? [2.4, 5.7, 9] : [2.4, 5.7];
      const openings = levels.flatMap((y) =>
        Array.from({ length: bays }, (_, k) => opening(((k + 0.5) * length) / bays, y, 1.16, 1.65))
      );
      if (end) openings.push(opening(length / 2, -0.02, 1.9, 2.25));
      if (end) openings.push(opening(length / 2, b.eaves + 0.6, 0.75, 1.05));
      if (end) {
        const raised = subFrame(hf, 0, 0, 0, -0.05);
        gableWall(
          builder,
          masonry,
          raised,
          [from, to],
          {
            eaveY: b.eaves + 0.05,
            apexY: b.eaves + (b.front / 2) * Math.tan(b.pitch) + 0.05,
          },
          openings.map((hole) => hole.map(([x, y]) => [x, y + 0.05]))
        );
      } else rectWall(builder, masonry, hf, [from, to], [-0.05, b.eaves], openings, 0);
      crossWindows(builder, styles.trim, hf, from, to, levels, bays);
    });
    horizontalCap(builder, wall, hf, ring, -0.05, false);
    gableRoof(builder, { wall, roof }, hf, {
      ...rect,
      eaveY: b.eaves,
      ridgeY: b.eaves + (b.front / 2) * Math.tan(b.pitch),
      ends: { start: false, end: false },
    });
    for (const face of [-b.depth / 2, b.depth / 2]) {
      for (const v of [-b.front / 2, b.front / 2]) {
        boxSolid(
          builder,
          styles.trim,
          hf,
          [face - 0.18, -0.03, v - 0.18],
          [face + 0.18, b.eaves + 0.1, v + 0.18],
          { top: true, bottom: true }
        );
      }
      const peak = b.eaves + (b.front / 2) * Math.tan(b.pitch);
      pyramidOverRing(
        builder,
        { roof: styles.trim, wall: styles.trim },
        hf,
        [
          [face - 0.22, -0.22],
          [face + 0.22, -0.22],
          [face + 0.22, 0.22],
          [face - 0.22, 0.22],
        ],
        peak - 0.1,
        { plan: [face, 0], y: peak + 1.2 }
      );
    }
    // One chimney on each distinct roof, rather than a repeated row of little houses.
    const ridge = b.eaves + (b.front / 2) * Math.tan(b.pitch);
    boxSolid(builder, wall, hf, [-3, ridge - 2, -0.5], [-2.2, ridge + 1, 0.5], {
      top: true,
      bottom: true,
    });
    boxSolid(builder, styles.trim, hf, [-3.12, ridge + 0.88, -0.62], [-2.08, ridge + 1.12, 0.62], {
      top: true,
      bottom: true,
    });
    boxSolid(
      builder,
      { ...styles.trim, color: roof.color },
      hf,
      [rect.u0 - 0.03, ridge - 0.04, -0.08],
      [rect.u1 + 0.03, ridge + 0.1, 0.08],
      { top: true, bottom: true }
    );
  }
}

export function damBuildingRings(plan: PlanFrame): readonly Ring[] {
  return DAM_BUILDINGS.map((b) => {
    const [alongU, alongV] = b.faces === 'damrak' ? [b.front, b.depth] : [b.depth, b.front];
    return [
      [b.u - alongU / 2, b.v - alongV / 2],
      [b.u + alongU / 2, b.v - alongV / 2],
      [b.u + alongU / 2, b.v + alongV / 2],
      [b.u - alongU / 2, b.v + alongV / 2],
    ].map((p) => toLayout(plan, [p[0]!, p[1]!]));
  });
}
